import { countLabel } from '@/utils/plural'
import React, { useCallback, useState } from 'react'
import DayMap from './DayMap/DayMap'
import DayPane, { DayEditing } from './DayPane/DayPane'
import DestinationLegend from './DestinationLegend/DestinationLegend'
import { CalendarColumn, DayColumn, ItineraryCard, MapColumn } from './ItineraryPage.styles'
import TripCalendar from './TripCalendar/TripCalendar'
import TripSummaryHeader from './TripSummaryHeader/TripSummaryHeader'
import { ItineraryModel } from './useItineraryModel'

/** What the member mode adds to the card; the Shared trip view passes none. */
export interface ItineraryEditing {
	onEditDestinations: () => void
	day: DayEditing
}

export interface ItineraryBoardProps {
	model: ItineraryModel
	onSelectDay: (dayIndex: number) => void
	/** The full map for the selected day, or for the whole trip (`null`) when that is what the card shows. */
	onOpenFullMap: (dayIndex: number | null) => void
	/** Absent in public mode: the card is then read-only. */
	editing?: ItineraryEditing
}

/**
 * The itinerary as one card: the trip summary, calendar and legend; the
 * selected day; and its map. The same card in both modes — member mode passes
 * `editing`, the Shared trip view does not.
 */
const ItineraryBoard: React.FC<ItineraryBoardProps> = ({ model, onSelectDay, onOpenFullMap, editing }) => {
	const { selectedDay, header, dayInfo } = model
	const [wholeTrip, setWholeTrip] = useState(false)
	const [highlight, setHighlight] = useState<{ eventId: number; tick: number } | null>(null)

	const handleSelectDay = useCallback(
		(dayIndex: number) => {
			onSelectDay(dayIndex)
			setWholeTrip(false)
			setHighlight(null)
		},
		[onSelectDay],
	)

	const handlePinClick = useCallback(
		(eventId: number, dayIndex: number) => {
			if (dayIndex !== selectedDay) onSelectDay(dayIndex)
			setWholeTrip(false)
			setHighlight(previous => ({ eventId, tick: (previous?.tick ?? 0) + 1 }))
		},
		[onSelectDay, selectedDay],
	)

	const dayLabel = header.place ?? header.title
	const mapLabel = wholeTrip ? `Whole trip · ${countLabel(model.dayCount, 'day')}` : dayLabel

	return (
		<ItineraryCard>
			<CalendarColumn>
				<TripSummaryHeader summary={model.summary} onEditDestinations={editing?.onEditDestinations} />
				<TripCalendar model={model.calendar} onSelectDay={handleSelectDay} />
				<DestinationLegend
					rows={model.legend}
					selectedDay={selectedDay}
					activeIndex={model.activeSpanIndex}
					onSelectDay={handleSelectDay}
				/>
			</CalendarColumn>
			<DayColumn>
				<DayPane
					day={{
						dayIndex: selectedDay,
						header,
						note: dayInfo?.note?.trim() || null,
						hasName: !!dayInfo?.name?.trim(),
					}}
					days={model.dayOptions}
					events={model.dayEvents}
					segments={model.daySegments}
					mappedEventIds={model.mappedEventIds}
					highlight={highlight}
					editing={editing?.day}
				/>
			</DayColumn>
			<MapColumn>
				<DayMap
					model={wholeTrip ? model.tripMap : model.dayMap}
					label={mapLabel}
					dayPicker={
						wholeTrip
							? undefined
							: {
									label: dayLabel,
									colorIndex: model.colorIndex,
									selectedDay,
									days: model.dayOptions,
									onSelectDay: handleSelectDay,
								}
					}
					wholeTripAvailable={model.tripMap.pins.length > 0}
					onToggleWholeTrip={() => setWholeTrip(v => !v)}
					onOpenFullMap={() => onOpenFullMap(wholeTrip ? null : selectedDay)}
					onPinClick={handlePinClick}
					noPinsHint={editing ? 'Pins show up here as you add events' : undefined}
				/>
			</MapColumn>
		</ItineraryCard>
	)
}

export default ItineraryBoard
